// LQP1: compact, full-screen QR packets with selective acknowledgement.
//
// Wire tokens use fixed-width binary fields encoded as base64url/hex, without
// verbose separators. At ECC Q, 16/24/32-character DATA chunks normally stay
// around QR versions 4–6: large modules for iPad and distant cameras.

import { crc32c } from '../protocol/crc32c.js';
import { decodeQrRgba, renderQrRgba } from './qr.js';

const PREFIX = 'LQP1';
const DEFAULT_CHUNK_SIZE = 16;
const MAX_PACKETS = 255;
const ASCII = /^[\x20-\x7e]+$/u;
const HEX32 = /^[0-9a-f]{8}$/u;
const TOKEN32 = '[A-Za-z0-9_-]{6}';
const DATA_PATTERN = new RegExp(`^${PREFIX}D(${TOKEN32})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})(${TOKEN32})(${TOKEN32})([\\x20-\\x7e]+)$`, 'u');
const ACK_PATTERN = new RegExp(`^${PREFIX}A(${TOKEN32})([0-9a-f]{2})(${TOKEN32})(${TOKEN32})$`, 'u');
const POLL_PATTERN = new RegExp(`^${PREFIX}P(${TOKEN32})([0-9a-f]{2})(${TOKEN32})(${TOKEN32})(${TOKEN32})$`, 'u');
const STATUS_PATTERN = new RegExp(`^${PREFIX}S(${TOKEN32})([0-9a-f]{2})(${TOKEN32})(${TOKEN32})([A-Za-z0-9_-]+)(${TOKEN32})$`, 'u');

export const QR_PACKET_PALETTES = Object.freeze([
  Object.freeze({ id: 'rose', dark: '#29070f', light: '#ffe8ed', label: 'Rose' }),
  Object.freeze({ id: 'mint', dark: '#062419', light: '#e4fff1', label: 'Menthe' }),
  Object.freeze({ id: 'sky', dark: '#07182e', light: '#e6f1ff', label: 'Ciel' }),
]);

export const QR_PACKET_MONO_PALETTE = Object.freeze({
  id: 'mono', dark: '#000000', light: '#ffffff', label: 'Noir / blanc',
});

/** Create rendered DATA packets for one optical transfer. */
export function createDataPackets(text, {
  chunkSize = DEFAULT_CHUNK_SIZE,
  sessionId = randomSessionId(),
  errorCorrectionLevel = 'Q',
  scale = 8,
  margin = 4,
  palette = 'pastel',
} = {}) {
  assertTransferText(text);
  assertChunkSize(chunkSize);
  assertSessionId(sessionId);
  const chunks = splitBySize(text, chunkSize);
  if (chunks.length > MAX_PACKETS) {
    throw new RangeError(`transfert QR trop long: ${chunks.length} paquets > ${MAX_PACKETS}`);
  }

  const transferCrc = crcHex(text);
  const total = chunks.length;
  const packets = chunks.map((chunk, index) => {
    const length = chunk.length;
    const packetCrc = crcHex(dataCrcInput(sessionId, index, total, length, transferCrc, chunk));
    const encodedText = `${PREFIX}D${encodeU32(sessionId)}${hexByte(index)}${hexByte(total)}${hexByte(length)}`
      + `${encodeU32(packetCrc)}${encodeU32(transferCrc)}${chunk}`;
    const packet = {
      ok: true,
      version: 1,
      type: 'DATA',
      sessionId,
      index,
      total,
      length,
      packetCrc,
      dataPacketCrc: packetCrc,
      transferCrc,
      chunk,
      encodedText,
    };
    return renderQrPacket(packet, { errorCorrectionLevel, scale, margin, palette });
  });

  return { version: 1, sessionId, transferCrc, total, chunkSize, packets };
}

/** Create a minimal ACK with its own CRC, tied to one exact DATA packet. */
export function createAckPacket(dataPacket, options = {}) {
  const data = normalizePacket(dataPacket);
  if (!data.ok || data.type !== 'DATA') throw new TypeError('paquet DATA requis pour créer un ACK');
  const ackCrc = crcHex(ackCrcInput(data.sessionId, data.index, data.dataPacketCrc));
  const encodedText = `${PREFIX}A${encodeU32(data.sessionId)}${hexByte(data.index)}`
    + `${encodeU32(data.dataPacketCrc)}${encodeU32(ackCrc)}`;
  return renderQrPacket({
    ok: true,
    version: 1,
    type: 'ACK',
    sessionId: data.sessionId,
    index: data.index,
    dataPacketCrc: data.dataPacketCrc,
    ackCrc,
    encodedText,
  }, { ...options, palette: options.palette ?? 'mono' });
}

/** Mark the end of a DATA round and request one cumulative STATUS bitmap. */
export function createPollPacket(sessionId, total, transferCrc, {
  roundId = randomSessionId(),
  ...renderOptions
} = {}) {
  assertSessionId(sessionId);
  assertSessionId(transferCrc);
  assertSessionId(roundId);
  assertTotal(total);
  const pollCrc = crcHex(pollCrcInput(sessionId, total, transferCrc, roundId));
  const encodedText = `${PREFIX}P${encodeU32(sessionId)}${hexByte(total)}`
    + `${encodeU32(transferCrc)}${encodeU32(roundId)}${encodeU32(pollCrc)}`;
  return renderQrPacket({
    ok: true,
    version: 1,
    type: 'POLL',
    sessionId,
    total,
    transferCrc,
    roundId,
    pollCrc,
    encodedText,
  }, { ...renderOptions, palette: renderOptions.palette ?? 'mono' });
}

/** Create a CRC-protected STATUS bitmap for a selective-ACK round. */
export function createStatusPacket(sessionId, total, receivedIndices, {
  transferCrc,
  roundId,
  transferValidated = false,
  ...renderOptions
} = {}) {
  assertSessionId(sessionId);
  assertSessionId(transferCrc);
  assertSessionId(roundId);
  assertTotal(total);
  const received = normalizeIndices(receivedIndices, total);
  if (received.length === total && transferValidated !== true) {
    throw new Error('STATUS complet interdit sans validation du CRC global');
  }
  const bitmapBytes = encodeBitmap(total, received);
  const bitmap = bytesToBase64Url(bitmapBytes);
  const statusCrc = crcHex(statusCrcInput(sessionId, total, transferCrc, roundId, bitmap));
  const encodedText = `${PREFIX}S${encodeU32(sessionId)}${hexByte(total)}`
    + `${encodeU32(transferCrc)}${encodeU32(roundId)}${bitmap}${encodeU32(statusCrc)}`;
  return renderQrPacket({
    ok: true,
    version: 1,
    type: 'STATUS',
    sessionId,
    total,
    transferCrc,
    roundId,
    bitmap,
    statusCrc,
    received,
    missing: missingIndices(total, received),
    encodedText,
  }, { ...renderOptions, palette: renderOptions.palette ?? 'mono' });
}

/** Parse and validate a canonical DATA, ACK or STATUS LQP1 token. */
export function parseQrPacket(text) {
  if (typeof text !== 'string') return reject('QR_PACKET_INVALID', 'contenu QR non textuel');
  if (text.startsWith(`${PREFIX}D`)) return parseData(text);
  if (text.startsWith(`${PREFIX}A`)) return parseAck(text);
  if (text.startsWith(`${PREFIX}P`)) return parsePoll(text);
  if (text.startsWith(`${PREFIX}S`)) return parseStatus(text);
  return reject('QR_PACKET_INVALID', 'type ou version de paquet QR inconnu');
}

/** Decode a camera image, then strictly parse its LQP1 token. */
export function decodeQrPacket(data, width, height) {
  const qr = decodeQrRgba(data, width, height);
  if (!qr) return reject('QR_PACKET_MISSING', 'aucun paquet QR détecté');
  return { ...parseQrPacket(qr.text), qr };
}

/** Render any created or parsed LQP1 packet as one ordinary full-screen QR. */
export function renderQrPacket(packet, {
  errorCorrectionLevel = 'Q',
  scale = 8,
  margin = 4,
  palette = 'pastel',
} = {}) {
  if (!packet || packet.ok !== true || typeof packet.encodedText !== 'string') {
    throw new TypeError('paquet LQP1 valide requis pour le rendu');
  }
  const selectedPalette = resolvePalette(palette, packet.type === 'DATA' ? packet.index : 0);
  const rendered = renderQrRgba(packet.encodedText, {
    errorCorrectionLevel,
    scale,
    margin,
    dark: selectedPalette.dark,
    light: selectedPalette.light,
  });
  return { ...packet, ...rendered, palette: selectedPalette };
}

export function drawQrPacket(canvas, packet, options = {}) {
  const rendered = renderQrPacket(packet, options);
  canvas.width = rendered.width;
  canvas.height = rendered.height;
  const context = canvas.getContext('2d', { alpha: false });
  const image = context.createImageData(rendered.width, rendered.height);
  image.data.set(rendered.data);
  context.putImageData(image, 0, 0);
  return rendered;
}

/** True only when an ACK names the exact session/index/DATA CRC. */
export function ackMatchesDataPacket(ackPacket, dataPacket) {
  const ack = normalizePacket(ackPacket);
  const data = normalizePacket(dataPacket);
  return ack.ok && data.ok && ack.type === 'ACK' && data.type === 'DATA'
    && ack.sessionId === data.sessionId
    && ack.index === data.index
    && ack.dataPacketCrc === data.dataPacketCrc;
}

/** True only for the STATUS generated for this exact transfer and POLL round. */
export function statusMatchesPoll(statusPacket, pollPacket) {
  const status = normalizePacket(statusPacket);
  const poll = normalizePacket(pollPacket);
  return status.ok && poll.ok && status.type === 'STATUS' && poll.type === 'POLL'
    && status.sessionId === poll.sessionId
    && status.total === poll.total
    && status.transferCrc === poll.transferCrc
    && status.roundId === poll.roundId;
}

/** Validate a STATUS and select only DATA packets still reported missing. */
export function selectMissingDataPackets(transfer, statusPacket, pollPacket) {
  const status = normalizePacket(statusPacket);
  const poll = normalizePacket(pollPacket);
  if (!transfer || !Array.isArray(transfer.packets) || !statusMatchesPoll(status, poll)
      || transfer.sessionId !== status.sessionId || transfer.transferCrc !== status.transferCrc
      || transfer.total !== status.total) {
    return reject('QR_PACKET_STATUS_MISMATCH', 'STATUS étranger ou périmé');
  }
  const missing = new Set(status.missing);
  return {
    ok: true,
    complete: missing.size === 0,
    received: status.received,
    missing: status.missing,
    packets: transfer.packets.filter((packet) => missing.has(packet.index)),
  };
}

/** Stateful, order-independent DATA reassembly with idempotent duplicates. */
export class QrPacketAssembler {
  constructor() { this.reset(); }

  reset() {
    this.sessionId = null;
    this.transferCrc = null;
    this.total = null;
    this.packets = new Map();
  }

  accept(inputPacket) {
    const packet = normalizePacket(inputPacket);
    if (!packet.ok) return packet;
    if (packet.type !== 'DATA') return reject('QR_PACKET_NOT_DATA', 'l’assembleur attend un paquet DATA');

    if (this.sessionId === null) {
      this.sessionId = packet.sessionId;
      this.transferCrc = packet.transferCrc;
      this.total = packet.total;
    } else if (packet.sessionId !== this.sessionId
        || packet.transferCrc !== this.transferCrc || packet.total !== this.total) {
      return reject('QR_PACKET_TRANSFER_MISMATCH', 'paquet appartenant à un autre transfert', this.snapshotFields());
    }

    const previous = this.packets.get(packet.index);
    if (previous) {
      if (previous.dataPacketCrc !== packet.dataPacketCrc) {
        return reject('QR_PACKET_DUPLICATE_CONFLICT', `doublon contradictoire à l’index ${packet.index}`,
          this.snapshotFields());
      }
      return { ...this.snapshot(), duplicate: true, accepted: false };
    }

    this.packets.set(packet.index, packet);
    return { ...this.snapshot(), duplicate: false, accepted: true };
  }

  snapshot() {
    if (this.sessionId === null) {
      return { ok: true, complete: false, sessionId: null, total: null, received: [], missing: [] };
    }
    const fields = this.snapshotFields();
    if (fields.missing.length) return { ok: true, complete: false, ...fields };

    const packets = [...this.packets.values()].sort((a, b) => a.index - b.index);
    const text = packets.map((packet) => packet.chunk).join('');
    const actualTransferCrc = crcHex(text);
    if (actualTransferCrc !== this.transferCrc) {
      return reject('QR_PACKET_TRANSFER_CRC', 'CRC32C final du transfert QR invalide', {
        ...fields,
        expected: this.transferCrc,
        actual: actualTransferCrc,
      });
    }
    return { ok: true, complete: true, ...fields, text, packets };
  }

  createStatus(pollPacket, options = {}) {
    if (this.sessionId === null) throw new Error('aucun transfert QR actif');
    const poll = normalizePacket(pollPacket);
    if (!poll.ok || poll.type !== 'POLL' || poll.sessionId !== this.sessionId
        || poll.total !== this.total || poll.transferCrc !== this.transferCrc) {
      throw new Error('POLL étranger ou invalide');
    }
    const snapshot = this.snapshot();
    if (!snapshot.ok) throw new Error('CRC global invalide: STATUS complet interdit');
    return createStatusPacket(this.sessionId, this.total, [...this.packets.keys()], {
      ...options,
      transferCrc: this.transferCrc,
      roundId: poll.roundId,
      transferValidated: snapshot.complete,
    });
  }

  snapshotFields() {
    const received = [...this.packets.keys()].sort((a, b) => a - b);
    return {
      sessionId: this.sessionId,
      transferCrc: this.transferCrc,
      total: this.total,
      received,
      missing: missingIndices(this.total, received),
    };
  }
}

export function assembleQrPackets(inputPackets) {
  if (!Array.isArray(inputPackets) || inputPackets.length === 0) {
    return reject('QR_PACKET_PARTS_MISSING', 'aucun paquet DATA reçu');
  }
  const assembler = new QrPacketAssembler();
  for (const packet of inputPackets) {
    const result = assembler.accept(packet);
    if (!result.ok) return result;
  }
  return assembler.snapshot();
}

function parseData(text) {
  const match = DATA_PATTERN.exec(text);
  if (!match) return reject('QR_PACKET_INVALID', 'format DATA LQP1 invalide');
  const sessionId = decodeU32(match[1]);
  const index = Number.parseInt(match[2], 16);
  const total = Number.parseInt(match[3], 16);
  const length = Number.parseInt(match[4], 16);
  const packetCrc = decodeU32(match[5]);
  const transferCrc = decodeU32(match[6]);
  const chunk = match[7];
  if (!sessionId || !packetCrc || !transferCrc || total < 1 || total > MAX_PACKETS
      || index < 0 || index >= total || length !== chunk.length || length < 1) {
    return reject('QR_PACKET_INVALID', 'champs DATA LQP1 invalides');
  }
  const actualPacketCrc = crcHex(dataCrcInput(sessionId, index, total, length, transferCrc, chunk));
  if (packetCrc !== actualPacketCrc) {
    return reject('QR_PACKET_DATA_CRC', 'CRC32C du paquet DATA invalide', {
      expected: packetCrc,
      actual: actualPacketCrc,
      sessionId,
      index,
      total,
    });
  }
  return {
    ok: true,
    version: 1,
    type: 'DATA',
    sessionId,
    index,
    total,
    length,
    packetCrc,
    dataPacketCrc: packetCrc,
    transferCrc,
    chunk,
    encodedText: text,
  };
}

function parseAck(text) {
  const match = ACK_PATTERN.exec(text);
  if (!match) return reject('QR_PACKET_INVALID', 'format ACK LQP1 invalide');
  const sessionId = decodeU32(match[1]);
  const index = Number.parseInt(match[2], 16);
  const dataPacketCrc = decodeU32(match[3]);
  const ackCrc = decodeU32(match[4]);
  if (!sessionId || !dataPacketCrc || !ackCrc || index < 0 || index >= MAX_PACKETS) {
    return reject('QR_PACKET_INVALID', 'champs ACK LQP1 invalides');
  }
  const actualAckCrc = crcHex(ackCrcInput(sessionId, index, dataPacketCrc));
  if (ackCrc !== actualAckCrc) {
    return reject('QR_PACKET_ACK_CRC', 'CRC32C du paquet ACK invalide', {
      expected: ackCrc,
      actual: actualAckCrc,
    });
  }
  return {
    ok: true,
    version: 1,
    type: 'ACK',
    sessionId,
    index,
    dataPacketCrc,
    ackCrc,
    encodedText: text,
  };
}

function parsePoll(text) {
  const match = POLL_PATTERN.exec(text);
  if (!match) return reject('QR_PACKET_INVALID', 'format POLL LQP1 invalide');
  const sessionId = decodeU32(match[1]);
  const total = Number.parseInt(match[2], 16);
  const transferCrc = decodeU32(match[3]);
  const roundId = decodeU32(match[4]);
  const pollCrc = decodeU32(match[5]);
  if (!sessionId || !transferCrc || !roundId || !pollCrc || total < 1 || total > MAX_PACKETS) {
    return reject('QR_PACKET_INVALID', 'champs POLL LQP1 invalides');
  }
  const actualPollCrc = crcHex(pollCrcInput(sessionId, total, transferCrc, roundId));
  if (pollCrc !== actualPollCrc) {
    return reject('QR_PACKET_POLL_CRC', 'CRC32C du POLL invalide', {
      expected: pollCrc,
      actual: actualPollCrc,
    });
  }
  return {
    ok: true,
    version: 1,
    type: 'POLL',
    sessionId,
    total,
    transferCrc,
    roundId,
    pollCrc,
    encodedText: text,
  };
}

function parseStatus(text) {
  const match = STATUS_PATTERN.exec(text);
  if (!match) return reject('QR_PACKET_INVALID', 'format STATUS LQP1 invalide');
  const sessionId = decodeU32(match[1]);
  const total = Number.parseInt(match[2], 16);
  const transferCrc = decodeU32(match[3]);
  const roundId = decodeU32(match[4]);
  const bitmap = match[5];
  const statusCrc = decodeU32(match[6]);
  const bitmapBytes = base64UrlToBytes(bitmap);
  if (!sessionId || !transferCrc || !roundId || !statusCrc
      || total < 1 || total > MAX_PACKETS || !bitmapBytes
      || bitmapBytes.length !== Math.ceil(total / 8)) {
    return reject('QR_PACKET_STATUS_BITMAP', 'bitmap STATUS LQP1 invalide');
  }
  const actualStatusCrc = crcHex(statusCrcInput(sessionId, total, transferCrc, roundId, bitmap));
  if (statusCrc !== actualStatusCrc) {
    return reject('QR_PACKET_STATUS_CRC', 'CRC32C du STATUS invalide', {
      expected: statusCrc,
      actual: actualStatusCrc,
    });
  }
  const decoded = decodeBitmap(total, bitmapBytes);
  if (!decoded.ok) return decoded;
  return {
    ok: true,
    version: 1,
    type: 'STATUS',
    sessionId,
    total,
    transferCrc,
    roundId,
    bitmap,
    statusCrc,
    received: decoded.received,
    missing: missingIndices(total, decoded.received),
    encodedText: text,
  };
}

function normalizePacket(input) {
  if (typeof input === 'string') return parseQrPacket(input);
  if (!input || typeof input.encodedText !== 'string') {
    return reject('QR_PACKET_INVALID', 'paquet LQP1 absent ou incomplet');
  }
  return parseQrPacket(input.encodedText);
}

function encodeBitmap(total, received) {
  const bytes = new Uint8Array(Math.ceil(total / 8));
  for (const index of received) bytes[Math.floor(index / 8)] |= 1 << (7 - index % 8);
  return bytes;
}

function decodeBitmap(total, bytes) {
  const received = [];
  for (let index = 0; index < bytes.length * 8; index++) {
    const set = Boolean(bytes[Math.floor(index / 8)] & (1 << (7 - index % 8)));
    if (!set) continue;
    if (index >= total) return reject('QR_PACKET_STATUS_BITMAP', 'bits hors plage non nuls dans STATUS');
    received.push(index);
  }
  return { ok: true, received };
}

function normalizeIndices(indices, total) {
  if (!Array.isArray(indices) && !(indices instanceof Set)) {
    throw new TypeError('receivedIndices doit être un tableau ou un Set');
  }
  const result = [...new Set(indices)].sort((a, b) => a - b);
  if (result.some((index) => !Number.isInteger(index) || index < 0 || index >= total)) {
    throw new RangeError('indice STATUS hors plage');
  }
  return result;
}

function missingIndices(total, received) {
  const present = new Set(received);
  return Array.from({ length: total }, (_, index) => index).filter((index) => !present.has(index));
}

function resolvePalette(palette, index) {
  if (palette === 'mono') return QR_PACKET_MONO_PALETTE;
  if (palette === 'pastel') return QR_PACKET_PALETTES[index % QR_PACKET_PALETTES.length];
  if (Array.isArray(palette) && palette.length) return assertPalette(palette[index % palette.length]);
  if (palette && typeof palette === 'object') return assertPalette(palette);
  throw new TypeError(`palette de paquet QR invalide: ${String(palette)}`);
}

function assertPalette(palette) {
  if (!palette || !/^#[0-9a-f]{6}$/iu.test(palette.dark)
      || !/^#[0-9a-f]{6}$/iu.test(palette.light)) {
    throw new TypeError('palette de paquet QR invalide');
  }
  return palette;
}

function assertTransferText(text) {
  if (typeof text !== 'string' || !text.length) throw new TypeError('texte de transfert QR vide');
  if (!ASCII.test(text)) throw new TypeError('les paquets QR attendent une enveloppe ASCII');
}

function assertChunkSize(chunkSize) {
  if (!Number.isInteger(chunkSize) || chunkSize < 1 || chunkSize > 128) {
    throw new RangeError('chunkSize doit être un entier compris entre 1 et 128');
  }
}

function assertSessionId(sessionId) {
  if (typeof sessionId !== 'string' || !HEX32.test(sessionId)) {
    throw new TypeError('sessionId doit contenir 8 chiffres hexadécimaux minuscules');
  }
}

function assertTotal(total) {
  if (!Number.isInteger(total) || total < 1 || total > MAX_PACKETS) {
    throw new RangeError(`total doit être compris entre 1 et ${MAX_PACKETS}`);
  }
}

function hexByte(value) {
  if (!Number.isInteger(value) || value < 0 || value > 255) throw new RangeError('octet hors plage');
  return value.toString(16).padStart(2, '0');
}

function encodeU32(hex) {
  if (!HEX32.test(hex)) throw new TypeError('u32 hexadécimal invalide');
  const bytes = Uint8Array.from(hex.match(/.{2}/gu), (pair) => Number.parseInt(pair, 16));
  return bytesToBase64Url(bytes);
}

function decodeU32(token) {
  const bytes = base64UrlToBytes(token);
  if (!bytes || bytes.length !== 4) return null;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return encodeU32(hex) === token ? hex : null;
}

function bytesToBase64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function base64UrlToBytes(value) {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]+$/u.test(value)) return null;
  try {
    const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - value.length % 4) % 4);
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    return bytesToBase64Url(bytes) === value ? bytes : null;
  } catch {
    return null;
  }
}

function splitBySize(value, chunkSize) {
  const chunks = [];
  for (let offset = 0; offset < value.length; offset += chunkSize) chunks.push(value.slice(offset, offset + chunkSize));
  return chunks;
}

function dataCrcInput(sessionId, index, total, length, transferCrc, chunk) {
  return `${sessionId}|${index}|${total}|${length}|${transferCrc}|${chunk}`;
}

function ackCrcInput(sessionId, index, dataPacketCrc) {
  return `${sessionId}|${index}|${dataPacketCrc}`;
}

function pollCrcInput(sessionId, total, transferCrc, roundId) {
  return `${sessionId}|${total}|${transferCrc}|${roundId}`;
}

function statusCrcInput(sessionId, total, transferCrc, roundId, bitmap) {
  return `${sessionId}|${total}|${transferCrc}|${roundId}|${bitmap}`;
}

function randomSessionId() {
  const values = new Uint32Array(1);
  globalThis.crypto.getRandomValues(values);
  return values[0].toString(16).padStart(8, '0');
}

function crcHex(value) {
  return crc32c(new TextEncoder().encode(value)).toString(16).padStart(8, '0');
}

function reject(code, reason, details = {}) { return { ok: false, code, reason, ...details }; }
